/**
 * System Article editor dialog — Library-PR-X2.
 *
 * Extracted from routes/sysadmin/library.articles.tsx during the IA
 * convergence refactor. The unified /sysadmin/library/documents tab
 * needs to open this dialog for article authoring; rather than
 * inline-importing from a peer route file we lift the dialog into a
 * proper component so both the legacy redirect path and the new
 * unified surface can consume it.
 *
 * Wraps the shared <RichTextEditor> (Tiptap surface that matches the
 * tenant WikiEditor visually). Save/update goes through the sysadmin
 * library hooks — no apiClient calls here.
 */
import { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';
import { RichTextEditor } from '@/components/kb/RichTextEditor';
import {
  useCreateSysLibArticle,
  useUpdateSysLibArticle,
  type SysLibArticle,
} from '@/lib/use-sysadmin-library';

export interface SystemArticleEditorDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** null = create flow; non-null = edit flow. */
  article: SysLibArticle | null;
  /** Wiki folders to pick from. Caller pre-loads via useSysLibFolders. */
  folders: { id: string; name: string }[];
}

export function SystemArticleEditorDialog({
  open,
  onOpenChange,
  article,
  folders,
}: SystemArticleEditorDialogProps) {
  const isEdit = article !== null;
  const [title, setTitle] = useState(article?.title ?? '');
  const [content, setContent] = useState(article?.content ?? '');
  const [folderId, setFolderId] = useState<string | null>(article?.folderId ?? null);
  const [submitting, setSubmitting] = useState(false);

  // Reset on open change so editing the next article doesn't keep
  // stale state. Using state-keys would be cleaner; this is the
  // minimum viable + matches the pre-extraction implementation.
  useMemo(() => {
    if (open && article) {
      setTitle(article.title);
      setContent(article.content);
      setFolderId(article.folderId);
    }
    if (open && !article) {
      setTitle('');
      setContent('');
      setFolderId(null);
    }
  }, [open, article]);

  const createMut = useCreateSysLibArticle();
  const updateMut = useUpdateSysLibArticle(article?.id ?? '');

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && article) {
        await updateMut.mutateAsync({
          title: title.trim(),
          content,
          contentText: content.replace(/<[^>]*>/g, ''),
          folderId,
        });
        toast.success('Article updated');
      } else {
        await createMut.mutateAsync({
          title: title.trim(),
          content,
          contentText: content.replace(/<[^>]*>/g, ''),
          folderId: folderId ?? undefined,
        });
        toast.success('Article created (status: draft)');
      }
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit System Article' : 'New System Article'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes are visible to tenants only after publish.'
              : 'New articles start as draft. Publish to make them visible to tenants.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={500}
              placeholder="ANSI Z535.6 Standard..."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="folder">Folder</Label>
            <Select
              value={folderId ?? '__root__'}
              onValueChange={(v) => setFolderId(v === '__root__' ? null : v)}
            >
              <SelectTrigger id="folder">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__root__">Root (no folder)</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Content</Label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Start writing the system article body..."
              ariaLabel="Article content"
              minHeightClassName="min-h-[360px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
